'use client';

import { useMemo, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { OptionChip } from '@/shared-ui/atoms/option-chip';
import { Select } from '@/shared-ui/atoms/select';
import { Swatch } from '@/shared-ui/atoms/swatch';
import { SwatchIndicator } from '@/shared-ui/atoms/swatch-indicator';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';
import { ExpandIcon } from '@/shared-ui/icons/expand';
import { Separator } from '@/components/ui/separator';
import translations from '@/locales/en-GB/translations.json';

export interface AttributeShadeOption {
    id: string;
    name: string;
    color: string;
    availability?: 'in-stock' | 'out-of-stock';
    property?: 'matte' | 'shimmer';
    availabilityMessage?: string;
}

type RequireKey<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;
type StockShadeOption = RequireKey<AttributeShadeOption, 'availability'>;
type PropertyShadeOption = RequireKey<AttributeShadeOption, 'property'>;

export type AttributeSelectorRootProps = ComponentPropsWithoutRef<'section'>;

const Root = ({ className, children, ...props }: AttributeSelectorRootProps) => {
    return (
        <section className={cn('space-y-4', className)} data-slot="attribute-selector" {...props}>
            {children}
        </section>
    );
};

Root.displayName = 'AttributeSelector.Root';

export type AttributeSelectorHeadingProps = Omit<TypographyProps, 'variant' | 'weight'>;

const Heading = ({ className, children, ...props }: AttributeSelectorHeadingProps) => {
    return (
        <Typography className={cn('px-4 lg:px-0', className)} variant="h4" weight="regular" {...props}>
            {children}
        </Typography>
    );
};

Heading.displayName = 'AttributeSelector.Heading';

export type AttributeSelectorSizeValues = {
    id: string;
    label: string;
    isOutOfStock?: boolean;
    discontinued?: boolean;
    availabilityMessage?: string;
};

export type AttributeSelectorSizeProps = ComponentPropsWithoutRef<'div'> & {
    sizes?: Array<AttributeSelectorSizeValues>;
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    children?: ReactNode;
};

const Size = ({
    className,
    sizes,
    value,
    defaultValue,
    onValueChange,
    children,
    ...props
}: AttributeSelectorSizeProps) => {
    const initialSizeId = defaultValue ?? sizes?.[0]?.id;
    const [internalValue, setInternalValue] = useState<string | undefined>(initialSizeId);
    const selectedSizeId = value ?? internalValue;
    const selectedSize = sizes?.find((size) => size.id === selectedSizeId);

    const handleValueChange = (newValue: string) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }

        onValueChange?.(newValue);
    };

    return (
        <div
            className={cn(
                'flex h-full min-h-0 flex-col flex-wrap gap-4 overflow-y-auto lg:h-auto lg:overflow-visible px-4 lg:px-0',
                className
            )}
            data-slot="attribute-selector-size"
            {...props}>
            <div className="flex gap-2">
                {sizes
                    ? sizes.map((size) => {
                          const sizeLabel = size.label;
                          const sentenceCasedLabel =
                              sizeLabel.length > 0 ? sizeLabel[0].toUpperCase() + sizeLabel.slice(1).toLowerCase() : '';
                          return (
                              <OptionChip
                                  key={size.id}
                                  discontinued={size.discontinued}
                                  isOutOfStock={size.isOutOfStock}
                                  isSelected={selectedSizeId === size.id}
                                  onClick={() => handleValueChange(size.id)}>
                                  {sentenceCasedLabel}
                              </OptionChip>
                          );
                      })
                    : children}
            </div>
            {selectedSize?.availabilityMessage && (
                <Typography className="block text-sm font-light text-primary-hover" variant="p">
                    {selectedSize.availabilityMessage}
                </Typography>
            )}
        </div>
    );
};

Size.displayName = 'AttributeSelector.Size';

type AttributeSelectorShadeCommonProps = ComponentPropsWithoutRef<'div'> & {
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    triggerLabel?: string;
};

export type AttributeSelectorShadeProps =
    | (AttributeSelectorShadeCommonProps & {
          variant?: 'stock';
          shades: StockShadeOption[];
      })
    | (AttributeSelectorShadeCommonProps & {
          variant: 'shade';
          shades: PropertyShadeOption[];
      });

type ShadeVariant = NonNullable<AttributeSelectorShadeProps['variant']>;
type ShadeGroupValue =
    | NonNullable<AttributeShadeOption['availability']>
    | NonNullable<AttributeShadeOption['property']>;

const SHADE_GROUP_CONFIG: Record<
    ShadeVariant,
    {
        key: 'availability' | 'property';
        group1Value: ShadeGroupValue;
        group2Value: ShadeGroupValue;
        group1Label: string;
        group2Label: string;
        group1ClassName: string;
        group2ClassName: string;
    }
> = {
    stock: {
        key: 'availability',
        group1Value: 'in-stock',
        group2Value: 'out-of-stock',
        group1Label: translations.pdp.attributeSelector.inStockShades,
        group2Label: translations.pdp.attributeSelector.outOfStockShades,
        group1ClassName: 'text-snk-status-success-dark',
        group2ClassName: 'text-destructive',
    },
    shade: {
        key: 'property',
        group1Value: 'matte',
        group2Value: 'shimmer',
        group1Label: translations.pdp.attributeSelector.matteShades,
        group2Label: translations.pdp.attributeSelector.shimmerShades,
        group1ClassName: 'text-foreground',
        group2ClassName: 'text-foreground',
    },
};

type ShadeSelectOptionProps = {
    shade: AttributeShadeOption;
};

type ShadeMobileOptionProps = {
    isSelected: boolean;
    onSelect: (shadeId: string) => void;
    shade: AttributeShadeOption;
};

type ShadeSelectSwatchProps = {
    shade: AttributeShadeOption;
    isSelected: boolean;
    onSelect: (shadeId: string) => void;
};

const ShadeSelectSwatch = ({ shade, isSelected, onSelect }: ShadeSelectSwatchProps) => {
    return (
        <Swatch
            size="sm"
            aria-label={shade.name}
            checked={isSelected}
            colour={shade.color}
            status={shade.availability === 'out-of-stock' ? 'unavailable' : 'default'}
            className={cn('size-6 rounded-full outline-offset-2')}
            onCheckedChange={() => onSelect(shade.id)}
            hasTooltip
            tooltipText={`Shade ${shade.name}`}
        />
    );
};

type ShadeSwatchListProps = {
    shades: AttributeShadeOption[];
    selectedShadeId?: string;
    onSelect: (shadeId: string) => void;
    className?: string;
    ariaLabel: string;
};

const ShadeSwatchList = ({ shades, selectedShadeId, onSelect, className, ariaLabel }: ShadeSwatchListProps) => {
    return (
        <ul className={cn('flex flex-wrap gap-2', className)} aria-label={ariaLabel}>
            {shades.map((shade) => (
                <li key={shade.id}>
                    <ShadeSelectSwatch shade={shade} isSelected={shade.id === selectedShadeId} onSelect={onSelect} />
                </li>
            ))}
        </ul>
    );
};

const ShadeSelectOption = ({ shade }: ShadeSelectOptionProps) => {
    return (
        <Select.Option value={shade.id} textValue={shade.name} className="!h-auto flex-col items-start gap-1 px-4 py-2">
            <div className="flex items-center gap-3 w-full">
                <SwatchIndicator
                    aria-hidden="true"
                    className="size-6 shrink-0"
                    colour={shade.color}
                    status={shade.availability === 'out-of-stock' ? 'unavailable' : 'default'}
                />
                <Typography className="font-normal text-base tracking-[1.44px] uppercase" variant="h5">
                    {shade.name}
                </Typography>
            </div>

            {shade.availabilityMessage && (
                <Typography className="block text-sm font-light text-primary-hover" variant="p">
                    {shade.availabilityMessage}
                </Typography>
            )}
        </Select.Option>
    );
};

const ShadeMobileOption = ({ isSelected, onSelect, shade }: ShadeMobileOptionProps) => {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={isSelected}
            className={cn(
                'flex w-full flex-col gap-2 px-4 py-2 text-left',
                'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]',
                isSelected && 'bg-snk-cream'
            )}
            onClick={() => onSelect(shade.id)}>
            <div className="flex items-center gap-x-2">
                <span className="inline-flex shrink-0">
                    <SwatchIndicator
                        aria-hidden="true"
                        className={cn(
                            'size-6',
                            isSelected &&
                                'rounded-full shadow-[0_0_0_2px_var(--background),0_0_0_3px_var(--foreground)]'
                        )}
                        colour={shade.color}
                        status={shade.availability === 'out-of-stock' ? 'unavailable' : 'default'}
                    />
                </span>
                <span className="min-w-0">
                    <Typography className="block font-normal text-sm uppercase tracking-[1.2px]" variant="p">
                        {shade.name}
                    </Typography>
                </span>
            </div>
            {shade.availabilityMessage && (
                <Typography className="block text-sm font-light text-primary-hover" variant="p">
                    {shade.availabilityMessage}
                </Typography>
            )}
        </button>
    );
};

type ShadeMobileListProps = {
    group1: AttributeShadeOption[];
    group2: AttributeShadeOption[];
    groupConfig: (typeof SHADE_GROUP_CONFIG)[ShadeVariant];
    onSelect: (shadeId: string) => void;
    selectedShadeId?: string;
};

const ShadeMobileList = ({ group1, group2, groupConfig, onSelect, selectedShadeId }: ShadeMobileListProps) => {
    return (
        <div
            className="h-full min-h-0 space-y-3 overflow-y-auto overflow-x-visible lg:hidden"
            role="radiogroup"
            aria-label="Shade options">
            {group1.length > 0 && (
                <div className="overflow-visible">
                    <Typography className={cn('mb-2 px-4', groupConfig.group1ClassName)} variant="p" as="h5">
                        {groupConfig.group1Label}
                    </Typography>
                    <div className="overflow-visible">
                        {group1.map((shade) => (
                            <ShadeMobileOption
                                key={shade.id}
                                isSelected={shade.id === selectedShadeId}
                                onSelect={onSelect}
                                shade={shade}
                            />
                        ))}
                    </div>
                </div>
            )}

            {group2.length > 0 && (
                <div className="border-t border-border pt-3">
                    <Typography className={cn('mb-2 px-4', groupConfig.group2ClassName)} variant="p" as="h5">
                        {groupConfig.group2Label}
                    </Typography>
                    <div>
                        {group2.map((shade) => (
                            <ShadeMobileOption
                                key={shade.id}
                                isSelected={shade.id === selectedShadeId}
                                onSelect={onSelect}
                                shade={shade}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Shade = ({
    shades,
    value,
    defaultValue,
    onValueChange,
    triggerLabel = 'Select shade',
    variant = 'stock',
    className,
    ...props
}: AttributeSelectorShadeProps) => {
    const groupConfig = SHADE_GROUP_CONFIG[variant];
    const group1 = shades.filter((shade) => shade.color && shade[groupConfig.key] === groupConfig.group1Value);
    const group2 = shades.filter((shade) => shade.color && shade[groupConfig.key] === groupConfig.group2Value);
    const initialShadeId = defaultValue ?? shades[0]?.id;
    const [internalValue, setInternalValue] = useState<string | undefined>(initialShadeId);
    const selectedShadeId = value ?? internalValue;

    const selectedShade = useMemo(
        () => shades.find((shade) => shade.id === selectedShadeId) ?? shades[0],
        [shades, selectedShadeId]
    );

    const handleValueChange = (newValue: string) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }

        onValueChange?.(newValue);
    };

    return (
        <div
            data-variant={variant}
            className={cn('group h-full min-h-0 overflow-hidden lg:h-auto lg:space-y-4 lg:overflow-visible', className)}
            data-slot="attribute-selector-shade"
            {...props}>
            <div className="hidden lg:block">
                <Select.Root placeholder={triggerLabel} value={selectedShadeId} onValueChange={handleValueChange}>
                    <Select.Trigger aria-label={triggerLabel}>
                        <span className="mr-3 inline-flex items-center">
                            <SwatchIndicator
                                aria-hidden="true"
                                className="size-[18px] shrink-0"
                                colour={selectedShade?.color}
                                status={selectedShade?.availability === 'out-of-stock' ? 'unavailable' : 'default'}
                            />
                        </span>
                        <span className="min-w-0 flex-1 truncate font-normal text-base tracking-[1.44px] uppercase">
                            {selectedShade?.name ?? triggerLabel}
                        </span>
                        <Select.TriggerIcon>
                            <Icon className="size-3" icon={ExpandIcon} decorative />
                        </Select.TriggerIcon>
                    </Select.Trigger>
                    <Select.Content className="px-0 py-0 max-h-[430px] overflow-y-auto">
                        {group1.length > 0 && (
                            <Typography
                                className={cn('px-4 py-2 !tracking-[0.16px]', groupConfig.group1ClassName)}
                                variant="p">
                                {groupConfig.group1Label}
                            </Typography>
                        )}

                        {group1.map((shade) => (
                            <ShadeSelectOption key={shade.id} shade={shade} />
                        ))}

                        {group2.length > 0 && (
                            <>
                                <Separator className="h-[1px] bg-muted-foreground" />
                                <Typography
                                    className={cn('px-4 pb-2 pt-4 !tracking-[0.16px]', groupConfig.group2ClassName)}
                                    variant="p">
                                    {groupConfig.group2Label}
                                </Typography>
                            </>
                        )}

                        {group2.map((shade) => (
                            <ShadeSelectOption key={shade.id} shade={shade} />
                        ))}
                    </Select.Content>
                </Select.Root>

                {group1.length > 0 && (
                    <ShadeSwatchList
                        shades={group1}
                        selectedShadeId={selectedShade?.id}
                        onSelect={handleValueChange}
                        className="mt-3 mx-1 mb-4"
                        ariaLabel="Shade options"
                    />
                )}

                {group2.length > 0 && (
                    <Typography className="mt-4 mb-3" variant="p">
                        {groupConfig.group2Label}
                    </Typography>
                )}

                {group2.length > 0 && (
                    <ShadeSwatchList
                        shades={group2}
                        selectedShadeId={selectedShade?.id}
                        onSelect={handleValueChange}
                        className="mx-1"
                        ariaLabel={`${groupConfig.group2Label} options`}
                    />
                )}
            </div>

            <ShadeMobileList
                group1={group1}
                group2={group2}
                groupConfig={groupConfig}
                onSelect={handleValueChange}
                selectedShadeId={selectedShade?.id}
            />
        </div>
    );
};

Shade.displayName = 'AttributeSelector.Shade';

export const AttributeSelector = {
    Root,
    Heading,
    Size,
    Shade,
} as const;
